import { helpCenterUrl, helpSpaceUrl } from "@/lib/help-center-seo";
import type { ReactElement } from "react";
import { HelpJsonLd } from "./help-json-ld";

/** TechArticle + full BreadcrumbList (start › space › ancestors › article). */
export function HelpArticleJsonLd({
  org_slug,
  organization_name,
  all_topics_label,
  space,
  ancestors,
  page,
}: {
  readonly org_slug: string;
  readonly organization_name: string;
  readonly all_topics_label: string;
  readonly space: { readonly space_id: string; readonly name: string };
  readonly ancestors: readonly {
    readonly page_id: string;
    readonly title: string;
  }[];
  readonly page: {
    readonly page_id: string;
    readonly title: string;
    readonly description: string;
    readonly last_edited_at: number;
  };
}): ReactElement {
  const canonical = helpCenterUrl(org_slug, page.page_id);
  const modifiedIso = new Date(page.last_edited_at).toISOString();
  const description = page.description.trim() || undefined;
  const trail = [
    { name: all_topics_label, item: helpCenterUrl(org_slug) },
    { name: space.name, item: helpSpaceUrl(org_slug, space.space_id) },
    ...ancestors.map((ancestor) => ({
      name: ancestor.title,
      item: helpCenterUrl(org_slug, ancestor.page_id),
    })),
    { name: page.title, item: canonical },
  ];
  return (
    <>
      <HelpJsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "TechArticle",
          headline: page.title,
          ...(description ? { description } : {}),
          inLanguage: "de",
          datePublished: modifiedIso,
          dateModified: modifiedIso,
          mainEntityOfPage: canonical,
          publisher: { "@type": "Organization", name: organization_name },
        }}
      />
      <HelpJsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: trail.map((step, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: step.name,
            item: step.item,
          })),
        }}
      />
    </>
  );
}
